/**
 * Redux slice for Inspection History (GraphQL version)
 * Uses GraphQL API via services-px instead of REST API
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { resetALL } from 'Redux/actions';
import { clientQuery, clientMutation } from '../../../../../core/data/GraphQLApi';
import { endpoints } from '../../../../../core/defaultValues';
import {
	getInspectionTypes as fetchInspectionTypes,
	getInspectionCategories as fetchInspectionCategories,
	getInspectionLayouts as fetchInspectionLayouts,
	searchAssets,
	searchInspectors,
} from '../../../../../services-px/inspectionApi';
import { TABLE_FILTER_TYPES } from '../filterCard/constants/tableFilterConfig';

// Build search conditions for inspection queries
const buildInspectionSearchConditions = (params) => {
	const {
		startTime,
		endTime,
		dates,
		assetIds,
		assetTagIds,
		inspectorIds,
		inspectionTypeIds,
		inspectionNameIds,
		severities,
		verification,
		zoneLayoutIds,
		inspectionIds,
		tagIds,
	} = params;

	const conditions = [];
	const effectiveStartTime = startTime || dates?.[0]?.startTime;
	const effectiveEndTime = endTime || dates?.[0]?.endTime;

	if (effectiveStartTime && effectiveEndTime) {
		conditions.push({
			range: {
				fields: ['startTime'],
				GTE: effectiveStartTime,
				LTE: effectiveEndTime,
			},
		});
	}

	if (assetIds && assetIds.length > 0) {
		conditions.push({
			fields: ['asset_ids'],
			terms: assetIds,
			exact: true,
		});
	}

	if ((assetTagIds && assetTagIds.length > 0) || (tagIds && tagIds.length > 0)) {
		const uniqueTagIds = Array.from(new Set([...(assetTagIds || []), ...(tagIds || [])]));
		conditions.push({
			fields: ['asset_tagIds'],
			terms: uniqueTagIds.map(String),
			exact: true,
		});
	}

	if (zoneLayoutIds && zoneLayoutIds.length > 0) {
		conditions.push({
			fields: ['inspectionLayoutId'],
			terms: zoneLayoutIds,
			exact: true,
		});
	}

	if (inspectorIds && inspectorIds.length > 0) {
		conditions.push({
			fields: ['driverId'],
			terms: inspectorIds,
			exact: true,
		});
	}

	if (inspectionTypeIds && inspectionTypeIds.length > 0) {
		conditions.push({
			fields: ['categoryId'],
			terms: inspectionTypeIds,
			exact: true,
		});
	}

	if (inspectionNameIds && inspectionNameIds.length > 0) {
		conditions.push({
			fields: ['typeId'],
			terms: inspectionNameIds,
			exact: true,
		});
	}

	if (severities && severities.length > 0) {
		conditions.push({
			fields: ['maxSeverity'],
			terms: severities,
			exact: true,
		});
	}

	if (verification && verification.length > 0) {
		verification.forEach((item) => {
			conditions.push({
				fields: [item],
				terms: ['true'],
				exact: true,
			});
		});
	}

	if (inspectionIds && inspectionIds.length > 0) {
		conditions.push({
			fields: ['id'],
			terms: inspectionIds.map(String),
			exact: true,
		});
	}

	return conditions;
};

const createDefaultSearchEntry = () => ({
	data: [],
	loading: false,
	error: null,
	lastQuery: '',
});

const createInitialSearchState = () => TABLE_FILTER_TYPES.reduce((acc, type) => {
	acc[type] = createDefaultSearchEntry();
	return acc;
}, {});

const initialState = {
	inspectionList: null,
	inspectionListCount: 0,
	loading: false,
	error: null,
	inspectionTypes: [],
	inspectionCategories: [],
	inspectionLayouts: [],
	search: createInitialSearchState(),
	// Inspectors (drivers) for fix modal
	inspectors: [],
	loadingInspectors: false,
	// Fix inspection state
	fixingInspection: false,
	fixInspectionError: null,
	lastFixedInspectionId: null,
	// Reload inspection state
	reloadingInspectionId: null,
	lastReloadedInspection: null,
	reloadInspectionError: null,
	// Delete inspection state
	deletingInspectionId: null,
	deleteInspectionError: null,
	// Export state
	exporting: false,
	exportDownloadUrl: null,
	exportError: null,
};

/**
 * Fetch inspection list using GraphQL API
 * @param {Object} params
 * @param {Object} params.queryStrings - Query parameters (page, perPage, sort, sortDir, filters)
 */
export const getInspectionList = createAsyncThunk(
	'evirPx/inspectionHistory/getInspectionList',
	async ({ queryStrings }, { rejectWithValue }) => {
		try {
			const { page = 1, perPage = 25, sort = 'startTime', sortDir = 'desc', ...filters } = queryStrings;

			// Map UI sort field names to GraphQL Inspection field names
			// Note: GraphQL enum enumInspectionSortField defines valid sort fields
			const sortFieldMap = {
				startTime: 'startTime',
				severity: 'maxSeverity',
				inspectorName: 'driver_name',
				type_name: 'type_name',
				category_name: 'category_name',
			};

			const graphqlSortField = sortFieldMap[sort] || 'startTime';

			const query = `
				query SearchInspections(
					$conditions: [InspectionSearchInput]
					$sort: [InspectionSortInput]
					$limit: Int
					$offset: Int
				) {
					searchInspections(
						conditions: $conditions
						sort: $sort
						limit: $limit
						offset: $offset
					) {
						items {
							id
							startTime
							typeId
							driver {
								name
							}
							maxSeverity
							type {
								id
								name
							}
							category {
								name
							}
							data
							failed
							failureReason
							asset_tagIds
						}
						total
					}
				}
			`;

			const variables = {
				conditions: buildInspectionSearchConditions({ page, perPage, sort, sortDir, ...filters }),
				sort: [{ field: graphqlSortField, order: sortDir.toUpperCase() }],
				limit: perPage,
				offset: (page - 1) * perPage,
			};

			const data = await clientQuery(
				query,
				variables,
				endpoints.GRAPHQL_GENERAL_V2
			);

			return {
				inspectionList: data.searchInspections.items,
				inspectionListCount: data.searchInspections.total || 0,
				page,
			};
		} catch (error) {
			console.error('[getInspectionList] Error:', error);
			return rejectWithValue(error.message || 'Failed to fetch inspections');
		}
	}
);

export const searchTableFilterRecords = createAsyncThunk(
	'evirPx/inspectionHistory/searchTableFilterRecords',
	async ({ pattern, type } = {}, { rejectWithValue, signal }) => {
		const sanitizedPattern = pattern?.trim?.() ?? '';

		if (!type) {
			return rejectWithValue({ type, message: 'Missing filter type' });
		}

		if (sanitizedPattern.length === 0) {
			return {
				type,
				pattern: sanitizedPattern,
				data: [],
			};
		}

		try {
			const actionList = {
				inspectors: searchInspectors,
				assets: searchAssets,
			};
			const response = await actionList[type](sanitizedPattern);

			return {
				type,
				pattern: sanitizedPattern,
				data: response,
			};
		} catch (error) {
			if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
				throw error;
			}

			return rejectWithValue({
				type,
				message: error.message || 'Failed to fetch table filter records',
			});
		}
	},
);

export const getInspectionTypes = createAsyncThunk(
	'evirPx/inspectionHistory/getInspectionTypes',
	async (_, { rejectWithValue }) => {
		try {
			const response = await fetchInspectionTypes();

			return {
				inspectionTypes: response,
			};
		} catch (error) {
			return rejectWithValue(error.message || 'Failed to fetch inspection types');
		}
	}
);

export const getInspectionCategories = createAsyncThunk(
	'evirPx/inspectionHistory/getInspectionCategories',
	async (_, { rejectWithValue }) => {
		try {
			const response = await fetchInspectionCategories();

			return {
				inspectionCategories: response,
			};
		} catch (error) {
			return rejectWithValue(error.message || 'Failed to fetch inspection categories');
		}
	}
);

export const getInspectionLayouts = createAsyncThunk(
	'evirPx/inspectionHistory/getInspectionLayouts',
	async (_, { rejectWithValue }) => {
		try {
			const response = await fetchInspectionLayouts();
			return {
				inspectionLayouts: response,
			};
		} catch (error) {
			return rejectWithValue(error.message || 'Failed to fetch inspection layouts');
		}
	}
);

export const exportInspections = createAsyncThunk(
	'evirPx/inspectionHistory/exportInspections',
	async ({ inspectionIds, queryStrings }, { rejectWithValue }) => {
		try {
			const { sort = 'startTime', sortDir = 'desc', ...filters } = queryStrings;

			const sortFieldMap = {
				startTime: 'startTime',
				severity: 'maxSeverity',
				inspectorName: 'driverId',
			};

			// Call exportInspections query - backend will internally call genericExport
			const query = `
				query ExportInspections(
					$conditions: [InspectionSearchInput]
					$sort: [InspectionSortInput]
					$sessionId: String!
					$timezone: String
				) {
					exportInspections(
						conditions: $conditions
						sort: $sort
						sessionId: $sessionId
						timezone: $timezone
					)
				}
			`;

			const data = await clientQuery(
				query,
				{
					conditions: buildInspectionSearchConditions({ ...filters, inspectionIds }),
					sort: [{ field: sortFieldMap[sort] || 'startTime', order: sortDir.toUpperCase() }],
					sessionId: sessionStorage.getItem('sessionId'),
					timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
				},
				endpoints.GRAPHQL_GENERAL_V2
			);

			// Backend returns the genericExport result
			return { result: data.exportInspections };
		} catch (error) {
			return rejectWithValue(error.message || 'Failed to export inspections');
		}
	}
);

/**
 * Fetch inspectors (drivers) for the fix modal
 */
export const fetchInspectors = createAsyncThunk(
	'evirPx/inspectionHistory/fetchInspectors',
	async (_, { rejectWithValue }) => {
		try {
			const query = `
				query GetInspectors {
					searchDrivers(limit: 1000) {
						items {
							id
							name
							title
							empNum
							externalId
							status
							email
							phone
						}
						total
					}
				}
			`;

			const data = await clientQuery(
				query,
				{},
				endpoints.GRAPHQL_GENERAL_V2
			);

			return { inspectors: data.searchDrivers.items };
		} catch (error) {
			return rejectWithValue(error.message || 'Failed to fetch inspectors');
		}
	}
);

/**
 * Fix a failed inspection by creating a review
 */
export const fixInspection = createAsyncThunk(
	'evirPx/inspectionHistory/fixInspection',
	async ({ inspectionId, driverFirstName, driverLastName, comment }, { rejectWithValue }) => {
		try {
			const mutation = `
				mutation CreateInspectionReview($id: ID!, $data: ReviewInfoInput!) {
					createInspectionReview(id: $id, data: $data) {
						id
						inspectionId
						driverAction
						data
						createdOn
						createdBy
						updatedOn
						updatedBy
					}
				}
			`;

			const reviewId = `review_${inspectionId}_${Date.now()}`;

			const data = await clientMutation(
				mutation,
				{
					id: reviewId,
					data: {
						inspectionId,
						driverAction: 'CERTIFIED',
						driverFirstName,
						driverLastName,
						comment: comment || 'Fixed via Inspection History',
						draft: false,
					},
				},
				endpoints.GRAPHQL_GENERAL_V2
			);

			return { inspectionId, review: data.createInspectionReview };
		} catch (error) {
			return rejectWithValue(error.message || 'Failed to fix inspection');
		}
	}
);

/**
 * Reload a single inspection to check if it's still failed
 */
export const reloadInspection = createAsyncThunk(
	'evirPx/inspectionHistory/reloadInspection',
	async ({ inspectionId }, { rejectWithValue }) => {
		try {
			const mutation = `
				mutation FixInspection($id: ID!) {
					fixInspection(id: $id) {
						id
						legacyId
						draft
						startTime
						driverId
						maxSeverity
						typeId
						categoryId
						requiresReview
						incomplete
						unverified
						failed
						failureReason
						inspectionDetails {
							inspectionTypeId
							inspectionTypeName
							inspectionCategoryId
							inspectionCategoryName
							inspectionDetailData {
								inspectionDetailDataName
								inspectionDetailDataType
								inspectionDetailCategory
								inspectionDetailContent
								inspectionDetailContentKey
								inspectionDetailIndex
								inspectionDetailMaxDecimal
							}
						}
						inspectionAssets {
							assetSubTypeId
							assetSubtypeName
							assetCategory
							assetId
							assetName
							zoneLayoutId
							zoneLayoutName
							assetViewGrid
							assetViewId
							inspectionZones {
								verificationType
								zoneName
								assetViewLocation
								sequence
								inspectionComponents {
									componentIndex
									componentName
									inspectionDefects {
										id
										condition
										severity
										layoutId
										comment
									}
								}
							}
						}
						data
						createdOn
						updatedOn
					}
				}
			`;

			const data = await clientMutation(
				mutation,
				{ id: inspectionId },
				endpoints.GRAPHQL_GENERAL_V2
			);

			return { inspectionId, inspection: data.fixInspection };
		} catch (error) {
			return rejectWithValue(error.message || 'Failed to reload inspection');
		}
	}
);

/**
 * Delete a failed inspection
 * Note: Only failed inspections can be deleted (enforced by backend)
 */
export const deleteInspection = createAsyncThunk(
	'evirPx/inspectionHistory/deleteInspection',
	async ({ inspectionId }, { rejectWithValue }) => {
		try {
			const mutation = `
				mutation DeleteInspection($id: ID!) {
					deleteInspection(id: $id)
				}
			`;

			await clientMutation(
				mutation,
				{ id: inspectionId },
				endpoints.GRAPHQL_GENERAL_V2
			);

			return { inspectionId };
		} catch (error) {
			return rejectWithValue(error.message || 'Failed to delete inspection');
		}
	}
);

const inspectionHistorySlice = createSlice({
	name: 'inspectionHistoryRedux',
	initialState,
	reducers: {
		clearInspectionList: (state) => {
			state.inspectionList = [];
			state.inspectionListCount = 0;
			state.error = null;
		},
		clearError: (state) => {
			state.error = null;
		},
		resetSearchResults: (state, action) => {
			const typesToReset = action.payload?.types || TABLE_FILTER_TYPES;
			typesToReset.forEach((type) => {
				if (state.search[type]) {
					state.search[type] = createDefaultSearchEntry();
				}
			});
		},
		clearExportState: (state) => {
			state.exportDownloadUrl = null;
			state.exportError = null;
		},
		clearFixState: (state) => {
			state.lastFixedInspectionId = null;
			state.fixInspectionError = null;
		},
		clearReloadState: (state) => {
			state.lastReloadedInspection = null;
			state.reloadInspectionError = null;
		},
		clearDeleteState: (state) => {
			state.deletingInspectionId = null;
			state.deleteInspectionError = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getInspectionList.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(getInspectionList.fulfilled, (state, action) => {
				state.loading = false;
				const { inspectionList, inspectionListCount } = action.payload;
				// Always replace the list (traditional pagination, not infinite scroll)
				state.inspectionList = inspectionList;
				state.inspectionListCount = inspectionListCount;
			})
			.addCase(getInspectionList.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload || 'Failed to fetch inspections';
				state.inspectionList = [];
				state.inspectionListCount = 0;
			})
			.addCase(searchTableFilterRecords.pending, (state, action) => {
				const type = action.meta.arg?.type;
				if (!type || !state.search[type]) {
					return;
				}

				state.search[type].loading = true;
				state.search[type].error = null;
				state.search[type].lastQuery = action.meta.arg?.pattern?.trim?.() ?? '';
			})
			.addCase(searchTableFilterRecords.fulfilled, (state, action) => {
				const { type, data, pattern } = action.payload || {};
				if (!type || !state.search[type]) {
					return;
				}

				state.search[type].loading = false;
				state.search[type].error = null;
				state.search[type].data = data || [];
				state.search[type].lastQuery = pattern ?? '';
			})
			.addCase(searchTableFilterRecords.rejected, (state, action) => {
				const type = action.meta.arg?.type;
				if (!type || !state.search[type]) {
					return;
				}

				state.search[type].loading = false;

				const isAbortError = action.error?.name === 'AbortError'
					|| action.error?.name === 'CanceledError'
					|| action.error?.code === 'ERR_CANCELED';
				if (isAbortError) {
					return;
				}

				state.search[type].error = action.payload?.message || action.error?.message || 'Failed to fetch table filter records';
			})
			// Filter Card - Inspection Name filter
			.addCase(getInspectionTypes.fulfilled, (state, action) => {
				const { inspectionTypes } = action.payload;
				state.inspectionTypes = inspectionTypes;
			})
			// Filter Card - Inspection Type filter
			.addCase(getInspectionCategories.fulfilled, (state, action) => {
				const { inspectionCategories } = action.payload;
				state.inspectionCategories = inspectionCategories;
			})
			// Filter Card - Asset Type filter
			.addCase(getInspectionLayouts.fulfilled, (state, action) => {
				const { inspectionLayouts } = action.payload;
				state.inspectionLayouts = inspectionLayouts;
			})
			// Export inspections
			.addCase(exportInspections.pending, (state) => {
				state.exporting = true;
				state.exportDownloadUrl = null;
				state.exportError = null;
			})
			.addCase(exportInspections.fulfilled, (state, action) => {
				state.exporting = false;
				state.exportDownloadUrl = action.payload.result;
			})
			.addCase(exportInspections.rejected, (state, action) => {
				state.exporting = false;
				state.exportError = action.payload || 'Failed to export inspections';
			})
			// Fetch inspectors for fix modal
			.addCase(fetchInspectors.pending, (state) => {
				state.loadingInspectors = true;
			})
			.addCase(fetchInspectors.fulfilled, (state, action) => {
				state.loadingInspectors = false;
				state.inspectors = action.payload.inspectors;
			})
			.addCase(fetchInspectors.rejected, (state) => {
				state.loadingInspectors = false;
			})
			// Fix inspection
			.addCase(fixInspection.pending, (state) => {
				state.fixingInspection = true;
				state.fixInspectionError = null;
				state.lastFixedInspectionId = null;
			})
			.addCase(fixInspection.fulfilled, (state, action) => {
				state.fixingInspection = false;
				// Update the inspection in the list to remove failed flag
				const { inspectionId } = action.payload;
				state.lastFixedInspectionId = inspectionId;
				if (state.inspectionList) {
					state.inspectionList = state.inspectionList.map((insp) =>
						insp.id === inspectionId
							? { ...insp, failed: false, failureReason: null }
							: insp
					);
				}
			})
			.addCase(fixInspection.rejected, (state, action) => {
				state.fixingInspection = false;
				state.fixInspectionError = action.payload || 'Failed to fix inspection';
			})
			// Reload inspection
			.addCase(reloadInspection.pending, (state, action) => {
				state.reloadingInspectionId = action.meta.arg?.inspectionId;
				state.lastReloadedInspection = null;
				state.reloadInspectionError = null;
			})
			.addCase(reloadInspection.fulfilled, (state, action) => {
				state.reloadingInspectionId = null;
				const { inspectionId, inspection } = action.payload;
				state.lastReloadedInspection = { inspectionId, inspection };
				// Update the inspection in the list if it's no longer failed
				if (state.inspectionList && !inspection.failed) {
					state.inspectionList = state.inspectionList.map((insp) =>
						insp.id === inspectionId
							? { ...insp, failed: false, failureReason: null }
							: insp
					);
				}
			})
			.addCase(reloadInspection.rejected, (state, action) => {
				state.reloadingInspectionId = null;
				state.reloadInspectionError = action.payload || 'Failed to reload inspection';
			})
			// Delete inspection
			.addCase(deleteInspection.pending, (state, action) => {
				state.deletingInspectionId = action.meta.arg?.inspectionId;
				state.deleteInspectionError = null;
			})
			.addCase(deleteInspection.fulfilled, (state, action) => {
				const { inspectionId } = action.payload;
				state.deletingInspectionId = null;
				// Remove the inspection from the list
				if (state.inspectionList) {
					state.inspectionList = state.inspectionList.filter(
						(insp) => insp.id !== inspectionId
					);
					state.inspectionListCount = Math.max(0, state.inspectionListCount - 1);
				}
			})
			.addCase(deleteInspection.rejected, (state, action) => {
				state.deletingInspectionId = null;
				state.deleteInspectionError = action.payload || 'Failed to delete inspection';
			})
			// Global reset
			.addCase(resetALL, () => initialState);
	},
});

export const {
	clearInspectionList,
	clearError,
	resetSearchResults,
	clearExportState,
	clearFixState,
	clearReloadState,
	clearDeleteState,
} = inspectionHistorySlice.actions;

export default inspectionHistorySlice.reducer;
